import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Shared hook that keeps the SAV unread count in sync across menu badges
 * (BottomTabBar + DesktopSidebar). Refreshes on:
 *   - initial mount
 *   - every `pollMs` (default 60s)
 *   - tab becoming visible again
 *   - custom `sav-updated` window event — dispatched by AlertesPanel after
 *     any mutation (delete, bulk-delete, status change) for instant UX.
 * Returns { count, refresh }.
 */
export default function useSavUnreadCount(pollMs = 60000) {
  const [count, setCount] = useState(0);

  const fetchCount = async () => {
    try {
      const r = await api.get("/sav-tickets/unread-count");
      setCount(r.data?.count || 0);
    } catch (e) {
      // Auth 401 is expected on login screen; log the rest for debug visibility
      if (e?.response?.status && e.response.status !== 401) {
        console.debug("[sav-unread] fetch failed:", e?.message || e);
      }
    }
  };

  useEffect(() => {
    fetchCount();
    const id = pollMs ? setInterval(fetchCount, pollMs) : null;
    const onVis = () => { if (!document.hidden) fetchCount(); };
    const onSavUpdated = () => fetchCount();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("sav-updated", onSavUpdated);
    return () => {
      if (id) clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("sav-updated", onSavUpdated);
    };
  }, [pollMs]);

  return { count, refresh: fetchCount };
}

/**
 * Fire from anywhere (e.g. after deleting a ticket) to force every sidebar
 * badge to refresh instantly instead of waiting for the next 60s poll.
 */
export function emitSavUpdated() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("sav-updated"));
  }
}

